import classNames from 'classnames';
import { StaticImageData } from 'next/image';
import { useCallback, useMemo, useRef, type RefObject } from 'react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Logo from '@alltrails/denali/components/Logo';
import PaidFeatureBadge from '@alltrails/denali/components/SubscriptionBadge/PaidFeatureBadge';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import { SubscriptionTier } from '@alltrails/types/SubscriptionTier';
import useLogMapOverlayDisplayed from '../../hooks/useLogMapOverlayDisplayed';
import { keycodeShortcuts } from '../../hooks/useStyleKeyboardShortcuts';
import useStyleSelection from '../../hooks/useStyleSelection';
import { useAppSelector } from '../../redux';
import { StyleId } from '../../types/Styles';
import adminUrlIsValid from '../../utils/adminUrlIsValid';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from '../../utils/styleCardConfigs';
import { formatStyleDisplayName, isBaseStyle, isStaticStyle, isStyleApplied } from '../../utils/styleIdHelpers';
import alltrailsOutdoorsV3Src from './alltrailsOutdoorsV3.png';
import styles from './GridBaseMapMenuItem.module.scss';

const adminStyleSourceUrlId = 'adminBasemap';

type Props = {
  loggedOverlayIds?: RefObject<Set<StyleId>>;
  styleId: StyleId;
  onUrlButtonClick?: () => void;
} & Parameters<typeof useStyleSelection>[0];

const GridBaseMapMenuItem = ({
  loggedOverlayIds,
  styleId,
  onUrlButtonClick,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck,
  handleRoadblock
}: Props) => {
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const { baseStyleId, dynamicStyleIds, staticStyleIds, adminStyleSettings } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    adminStyleSettings: state.map.adminStyleSettings
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });
  const itemRef = useRef<HTMLDivElement>(null);
  useLogMapOverlayDisplayed({ disableAnalytics, itemRef, loggedOverlayIds, styleId });

  const active = isStyleApplied(styleId, { baseStyleId, dynamicStyleIds, staticStyleIds });

  const { displayName, imgSrc, requiresAdmin, hideAdminBadge, tier } = useMemo<{
    displayName: MessageDescriptor | string;
    imgSrc?: string | StaticImageData;
    requiresAdmin?: boolean;
    hideAdminBadge?: boolean;
    tier?: SubscriptionTier;
  }>(() => {
    if (isBaseStyle(styleId)) {
      const { displayName, imgSrc, supports3d, requiresAdmin, tier } = baseStyleCardConfigs[styleId];
      const updatedImgSrc = styleId === 'alltrailsOutdoorsV3' ? alltrailsOutdoorsV3Src : imgSrc;
      return { displayName, imgSrc: updatedImgSrc, supports3d, requiresAdmin, tier };
    } else if (isStaticStyle(styleId)) {
      const { displayName, imgSrc, requiresAdmin } = staticStyleCardConfigs[styleId];
      return { displayName, imgSrc, requiresAdmin };
    } else {
      const { displayName, imgSrc, requiresAdmin, hideAdminBadge, tier } = dynamicStyleCardConfigs[styleId];
      return { displayName, imgSrc, requiresAdmin, hideAdminBadge, tier };
    }
  }, [styleId]);

  const keyboardShortcutText = useMemo(() => {
    const keycode = keycodeShortcuts[styleId];
    if (keycode && isAdmin && styleId !== 'heatmap') {
      return `Opt+${keycode.replace('Key', '')}`; // KeyA -> A
    }
    return undefined;
  }, [isAdmin, styleId]);

  const isDisabled = useMemo(() => {
    if (isAdmin) {
      if (styleId === adminStyleSourceUrlId && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!])) {
        return true;
      }
    }
    return false;
  }, [adminStyleSettings?.styleSourceUrls, isAdmin, styleId]);

  const urlButton = useMemo(() => {
    const requiresUrl = styleId === adminStyleSourceUrlId;
    if (requiresUrl && onUrlButtonClick) {
      return (
        <Button
          className={styles.urlButton}
          onClick={onUrlButtonClick}
          size="sm"
          stopPropagation
          text={
            !!adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!] ? (
              <FormattedMessage defaultMessage="Edit custom basemap" />
            ) : (
              <FormattedMessage defaultMessage="Add custom basemap" />
            )
          }
          width="full"
        />
      );
    }
    return null;
  }, [adminStyleSettings, onUrlButtonClick, styleId]);

  const handleStyleSelect = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onStyleSelect(styleId);
  }, [isDisabled, onStyleSelect, styleId]);

  return (
    <>
      <div
        className={styles.container}
        onClick={handleStyleSelect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleStyleSelect();
          }
        }}
        ref={itemRef}
        role="button"
        tabIndex={0}
      >
        <div className={styles.imageContainer}>
          <Image
            className={classNames(styles.image, { [styles.active]: active, [styles.disabled]: isDisabled })}
            alt={intl.formatMessage({ defaultMessage: 'Preview' })}
            src={imgSrc}
            width={105}
            height={70}
          />
          {active && (
            <div className={styles.activeCheckmarkContainer}>
              <CheckCircleFilled size="sm" />
            </div>
          )}
        </div>
        <div className={styles.textContainer}>
          <div className={styles.displayNameContainer}>
            <Typography variant="text100" component="div">
              {formatStyleDisplayName(intl, displayName)}
            </Typography>
          </div>
          {!isMobile && keyboardShortcutText && (
            <Typography variant="text100" color="secondary">
              {keyboardShortcutText}
            </Typography>
          )}
        </div>
        {requiresAdmin && !hideAdminBadge ? (
          <div className={styles.tierBadge}>
            <Logo color="monochrome-dark" size="xs" variant="symbol" />
          </div>
        ) : null}
        {(tier === 'plus' || tier === 'peak') && !requiresAdmin ? (
          <div className={styles.tierBadge}>
            <PaidFeatureBadge featureTier={tier} size="sm" variant="icon" />
          </div>
        ) : null}
      </div>
      {urlButton}
    </>
  );
};

export default GridBaseMapMenuItem;
